import { Component } from '@theme/component';

/**
 * Product finder.
 *
 * Highlights the products that suit the selected health category and dims the
 * rest. Every product stays in the DOM; selecting "everything" clears dimming.
 * Category buttons carry `data-category`; product cards carry a space-separated
 * `data-categories` list (derived from each product's `health:` tags).
 *
 * @extends {Component}
 */
class ProductFinder extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);

    // Default to the pre-selected button, falling back to the first category.
    const active =
      this.querySelector('[data-category][aria-pressed="true"]') ?? this.querySelector('[data-category]');
    this.#select(active?.getAttribute('data-category') ?? 'everything');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-category]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    this.#select(button.getAttribute('data-category') ?? 'everything');
  };

  /** @param {string} category */
  #select(category) {
    for (const button of this.querySelectorAll('[data-category]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-category') === category));
    }

    for (const card of this.querySelectorAll('[data-categories]')) {
      const categories = (card.getAttribute('data-categories') ?? '').split(' ').filter(Boolean);
      const matches = category === 'everything' || categories.includes(category);
      card.classList.toggle('is-dimmed', !matches);
    }
  }
}

if (!customElements.get('product-finder')) {
  customElements.define('product-finder', ProductFinder);
}
